import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { SortingRule } from 'react-table';

import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import useInputValue from '../../hooks/useInputValue';
import useToggle from '../../hooks/useToggle';
import RawVulnsStore from '../../redux/raw_vulns';
import { dispatchAsync } from '../../redux/utils';
import type { CurrentRawVuln } from '../../../shared/model-typings/currentrawvuln';
import type { GroupedRawVulns, IndexedRawVulns } from '../../../typings/api';

import ModalReviewRawVuln from './ModalReviewRawVuln';
import RawVulnEditHeader from './RawVulnEditHeader';
import RawVulnsTable from './RawVulnsTable';
import { getGroupedIndex } from './RawVulnUtils';

type SearchOptions = {
  search: string;
  page: number;
  pageSize: number;
  filterLinux: boolean;
  sortingState: SortingRule[];
};

const RawVulnEditPage: VoidFunctionComponent = () => {
  // Local state setup
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [search, setSearch] = useInputValue('');
  const [shouldFilterLinux, onToggleFilterLinux] = useToggle(true);
  const [sortingState, setSortingState] = useState<SortingRule[]>([]);
  const [vulnsToUpdate, setVulnsToUpdate] = useState<IndexedRawVulns | null>(null);
  const [rawVulnData, setRawVulnData] = useState<GroupedRawVulns>({});
  const [rawVulnsEditIndex, setRawVulnsEditIndex] = useState<string | null>(null);
  const [isShowingReviewModal, setShowingReviewModal] = useState(false);

  // Redux state setup
  const dispatch = useDispatch();
  const isLoading = useSelector(RawVulnsStore.selectors.getLoading);
  const rawVulnsRedux = useSelector(RawVulnsStore.selectors.getAllRawVulns);
  const rawVulnsCount = useSelector(RawVulnsStore.selectors.getRawVulnsCount);

  // Callback setup
  const debouncedFetchData = useDebouncedCallback(async (options: SearchOptions) => {
    const fetchOptions = {
      search: options.search.trim(),
      page: options.page,
      pageSize: options.pageSize,
      filterLinux: options.filterLinux,
      orderBy: options.sortingState[0]
        ? {
            field: options.sortingState[0].id,
            isDescending: options.sortingState[0].desc,
          }
        : undefined,
    };

    dispatch(RawVulnsStore.actions.fetchAll(fetchOptions));
  }, 250);

  const onSave = useCallback(async () => {
    if (vulnsToUpdate) {
      await dispatchAsync(dispatch, RawVulnsStore.actions.updateRawVulns(vulnsToUpdate));
      setVulnsToUpdate(null);
    }
    debouncedFetchData({
      search,
      pageSize,
      page,
      filterLinux: shouldFilterLinux,
      sortingState,
    });
  }, [debouncedFetchData, vulnsToUpdate, dispatch, page, pageSize, search, shouldFilterLinux, sortingState]);

  const onSetNewRawVulnValue = useCallback((updatedRawVulns: CurrentRawVuln[]) => {
    const newVulnsToUpdate = { ...(vulnsToUpdate ?? {}) };
    const newRawVulnsData = { ...rawVulnData };

    // Set vuln to be updated on server
    updatedRawVulns.forEach((updatedRawVuln) => {
      // store the list of raw vulns to be updated
      newVulnsToUpdate[updatedRawVuln.rawVulnAuditId] = updatedRawVuln;
    });

    // Update rawvuln in local state for display
    const { cve, projectName, fetcher } = _.pick(_.head(updatedRawVulns), [
      'cve',
      'projectName',
      'fetcher',
    ]);
    newRawVulnsData[getGroupedIndex(cve, projectName, fetcher)] = updatedRawVulns;

    setVulnsToUpdate(newVulnsToUpdate);
    setRawVulnData(newRawVulnsData);
  }, [rawVulnData, vulnsToUpdate]);

  /*
  * Effect setup
  */
  // Whenever something about our search changed, we want to re-fetch.
  useEffect(() => {
    debouncedFetchData({
      search,
      pageSize,
      page,
      filterLinux: shouldFilterLinux,
      sortingState,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to include debouncedFetchData in the dependency array.
  }, [page, pageSize, search, shouldFilterLinux, sortingState]);

  // Whenever the RawVulns in redux change, we want to make a local copy.
  useEffect(() => {
    setRawVulnData(rawVulnsRedux);
  }, [rawVulnsRedux]);

  return (
    <div className="margin-md">
      <Helmet title="RawVuln Tools" />
      <RawVulnEditHeader
        search={search}
        shouldFilterLinux={shouldFilterLinux}
        onSave={onSave}
        isLoading={isLoading}
        isSaveDisabled={!vulnsToUpdate}
        onSearchChange={setSearch}
        onToggleLinuxFilter={onToggleFilterLinux}
      />
      <RawVulnsTable
        rawVulnData={rawVulnData}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        rawVulnsCount={rawVulnsCount}
        setRawVulnsEditIndex={setRawVulnsEditIndex}
        setShowReviewModal={setShowingReviewModal}
        handleChangePage={(val) => setPage(val)}
        handleChangePageSize={(val) => setPageSize(val)}
        handleChangeSorting={(val) => setSortingState(val)}
      />
      <ModalReviewRawVuln
        show={isShowingReviewModal}
        rawVulnToEdit={rawVulnsEditIndex ? rawVulnData[rawVulnsEditIndex] : undefined}
        onHide={() => setShowingReviewModal(false)}
        onSave={onSetNewRawVulnValue}
      />
    </div>
  );
};

export default RawVulnEditPage;
